import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import { useSelector } from "react-redux";
import LHOverlay from "../../../components/LHOverlay";
import LhCardDisplay from "../../../components/LhCardDisplay";
import ExpandableImage from "../../../components/ExpandableImage";
import { RootState } from "../../../store";
import { useTheme } from "../../../theme";

// Interface for detailed leadership walk data
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DetailedLeadershipWalkData {
  id?: number;
  walkData?: {
    location?: string;
    walkType?: string;
    period?: number;
    week?: number;
    shift?: string | null;
    miscellImageDesc?: string | null;
    miscellImage2Desc?: string | null;
    managerSignature?: string | null;
    miscellImage?: string | null;
    miscellImage2?: string | null;
    updatedImg1?: boolean;
    updatedImg2?: boolean;
    updatedManagerSign?: boolean;
    attendees?: string;
    notes?: string;
    createdBy?: string;
    modifiedBy?: string | null;
    status?: string;
    walkDate?: string;
    startTime?: string;
    endTime?: string;
    observationHeader?: string;
    datetime?: string;
    location_Id?: number;
    walkTime?: number;
    completed?: boolean;
  };
  toolsMapping?: Array<{
    toolId: number;
    toolName?: string;
    [key: string]: any;
  }>;
  leadershipWalkImages?: Array<{
    ImageId: number;
    ImageBase64: string | null;
    Description: string | null;
    ObservationId: string | null;
    ImageType?: string;
    FileName?: string;
    [key: string]: any;
  }>;
  ID?: number;
  Title?: string;
  Notes?: string;
  Location_Name?: string;
  Walk_Type?: string;
  Created?: string;
  Period?: number;
  Week?: number;
  Shift?: string;
  Walk_Number?: number;
  Modified?: string;
  Created_By?: string;
  Modified_By?: string | null;
  status?: string;
  [key: string]: any;
}

interface LeadershipWalkModalProps {
  selectedRowData: any | null;
  isOverlayVisible: boolean;
  closeOverlay: () => void;
  isLoading?: boolean; // Optional prop to show loading state
}

const LeadershipWalkModal: React.FC<LeadershipWalkModalProps> = ({
  selectedRowData,
  isOverlayVisible,
  closeOverlay,
  isLoading = false, // Use provided isLoading prop with default value
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const [localLoading, setLocalLoading] = useState<boolean>(true);

  // Get data from Redux store
  const leadershipWalkQuestions = useSelector(
    (state: RootState) => state.leadershipWalk.questions
  );
  const selectedLeadershipWalk = useSelector(
    (state: RootState) => state.leadershipWalk.selectedLeadershipWalk
  );
  console.log(
    "LeadershipWalkModal - Selected Row Data:",
    leadershipWalkQuestions
  );
  // Keep only essential logging for troubleshooting
  // console.log('LeadershipWalkModal - Questions available:', !!leadershipWalkQuestions);

  // Combine data from props and redux for a complete data set
  const combinedData = useMemo(() => {
    if (!selectedRowData) {
      return null;
    }

    // Merge data from selectedLeadershipWalk and selectedRowData
    const mergedData = {
      ...selectedRowData,
      ...(selectedLeadershipWalk || {}),
    };

    // Handle different property naming conventions for walkData
    const walkData = mergedData.walkData || (mergedData as any).WalkData || {};

    // Handle different property naming conventions for tools
    const toolsMapping =
      mergedData.toolsMapping || (mergedData as any).ToolsMapping || [];

    // Handle different property naming conventions for images
    const leadershipWalkImages =
      mergedData.leadershipWalkImages ||
      mergedData.leadershipImages ||
      (mergedData as any).LeadershipWalkImages ||
      (mergedData as any).LeadershipImages ||
      [];

    // Enrich toolsMapping with question text if not already present
    const enhancedToolsMapping = toolsMapping.map((tool: any) => {
      if (!tool.toolName && tool.toolId) {
        // Look for a matching question to get its text
        const sections = [
          "Safety",
          "People",
          "Accuracy",
          "Costs",
        ] as (keyof typeof leadershipWalkQuestions)[];

        for (const section of sections) {
          if (leadershipWalkQuestions?.[section]) {
            const match = leadershipWalkQuestions[section].find((q: any) => {
              const questionId =
                q.ID?.toString() || q.Id?.toString() || q.id?.toString();
              return questionId === tool.toolId.toString();
            });

            if (match) {
              return {
                ...tool,
                toolName:
                  match.Question ||
                  match.question ||
                  match.questionText ||
                  match.QuestionText,
              };
            }
          }
        }
      }
      return tool;
    });

    return {
      ...mergedData,
      walkData,
      toolsMapping: enhancedToolsMapping,
      leadershipWalkImages,
    };
  }, [selectedRowData, selectedLeadershipWalk, leadershipWalkQuestions]);

  // Set loading to false once we receive the data
  useEffect(() => {
    if (combinedData) {
      setLocalLoading(false);
    }
  }, [combinedData]);

  // Helper function to render images (supports both Base64 and URL) with expandable functionality
  const renderImage = (
    imageSource: string | null,
    alt: string = "Image",
    style: any = {}
  ) => {
    if (!imageSource || typeof imageSource !== 'string') {
      console.log("renderImage: No valid image source provided", { imageSource });
      return null;
    }

    let imageUri: string;
    try {
      console.log("renderImage: Processing image", {
        alt,
        hasImageSource: !!imageSource,
        sourcePreview: imageSource.substring(0, 80) + "...",
        startsWithData: imageSource.startsWith("data:image"),
        startsWithHttp: imageSource.startsWith("http"),
        sourceLength: imageSource.length
      });

      // Check if it's already a complete data URI
      if (imageSource.startsWith("data:image")) {
        imageUri = imageSource;
        console.log("renderImage: Using data URI directly");
      } 
      // Check if it's an HTTPS/HTTP URL (like Azure Blob Storage)
      else if (imageSource.startsWith("https://") || imageSource.startsWith("http://")) {
        imageUri = imageSource;
        console.log("renderImage: Using HTTPS URL directly", { url: imageUri.substring(0, 80) + "..." });
      } 
      // Otherwise, treat it as raw Base64 data
      else {
        // Clean any whitespace and add the data URI prefix
        const cleanBase64 = imageSource.replace(/\s/g, '');
        imageUri = `data:image/jpeg;base64,${cleanBase64}`;
        console.log("renderImage: Created data URI from Base64", { 
          originalLength: imageSource.length,
          cleanLength: cleanBase64.length,
          finalUriLength: imageUri.length
        });
      }

      console.log("renderImage: Final imageUri ready", {
        alt,
        uriType: imageUri.startsWith("data:") ? "data-uri" : "url",
        uriPreview: imageUri.substring(0, 80) + "...",
        uriLength: imageUri.length
      });

      return (
        <View style={{ 
          width: style?.width || "100%", 
          minHeight: style?.height || 200,
          backgroundColor: isDarkMode ? colors.background.elevated : colors.background.surface,
          // borderWidth: 2,
          // borderColor: 'green', // Debug border
          marginBottom: 8,
        }}>
          <ExpandableImage
            imageUri={imageUri}
            alt={alt}
            style={{
              width: "100%",
              height: style?.height || 200,
              borderRadius: 4,
            }}
            containerStyle={{ 
              width: "100%",
              height: style?.height || 200,
            }}
          />
        </View>
      );
    } catch (error) {
      console.error("Error rendering image:", error, { imageSource: imageSource?.substring(0, 100) });
      return (
        <View style={[styles.errorImageContainer, style]}>
          <Text style={styles.errorImageText}>Unable to display image</Text>
        </View>
      );
    }
  };

  // Helper to get question text by toolId
  const getQuestionText = (toolId: number): string => {
    if (!leadershipWalkQuestions || !toolId) {
      return "";
    }

    // Try to find the question in any section (Safety, People, Accuracy, Costs, and also check Reliability)
    const sections: (keyof typeof leadershipWalkQuestions)[] = [
      "Safety",
      "People",
      "Accuracy",
      "Costs",
    ];

    // Also check Reliability section which might not be in the type definition
    const allSections = [...sections, "Reliability"];

    for (const section of allSections) {
      // TypeScript safe check for section existence
      const sectionQuestions = (leadershipWalkQuestions as any)[section];
      if (sectionQuestions && Array.isArray(sectionQuestions)) {
        const found = sectionQuestions.find((q: any) => {
          // Try all possible ID properties and formats
          const possibleIds = [
            q.ID?.toString(),
            q.Id?.toString(),
            q.id?.toString(),
            q.toolId?.toString(),
          ].filter(Boolean);

          const tId = toolId?.toString();

          // Check if any of the possible IDs match the tool ID
          return possibleIds.some((id) => id === tId);
        });

        if (found) {
          // Try all possible question text properties
          const questionText =
            found.Negative_Behavior ||
            found.question ||
            found.questionText ||
            found.QuestionText ||
            found.Text ||
            found.text ||
            "";
          console.log(`Found question for toolId ${toolId}:`, questionText);
          return questionText || "";
        }
      }
    }

    // Check if we have toolsMapping with names in the combined data
    if (combinedData && combinedData.toolsMapping) {
      const mappedTool = combinedData.toolsMapping.find(
        (t: any) => t.toolId?.toString() === toolId?.toString()
      );

      if (mappedTool && mappedTool.toolName) {
        return mappedTool.toolName;
      }
    }

    return "";
  };

  // Render content based on loading/error state
  const renderContent = () => {
    if (isLoading || localLoading || !combinedData) {
      return (
        <View
          style={[
            styles.loadingOverlayContainer,
            { backgroundColor: colors.background.page },
          ]}
        >
          <ActivityIndicator
            size="large"
            color={isDarkMode ? colors.text.inverse : colors.brand.primary}
          />
          <Text
            style={[
              styles.loadingTitle,
              { color: isDarkMode ? colors.text.inverse : "#0066CC" },
            ]}
          >
            Loading Leadership Walk Details...
          </Text>
        </View>
      );
    }

    // Using any here to avoid TypeScript errors since we're combining data from different sources
    const data: any = combinedData;
    const walkData = data.walkData || {};
    console.log("Combined data:", data);
    // Get observation questions
    const observationList: string[] = [];

    // Check for toolsMapping in the combined data
    if (
      data.toolsMapping &&
      Array.isArray(data.toolsMapping) &&
      data.toolsMapping.length > 0
    ) {
      data.toolsMapping.forEach((mapping: any) => {
        const questionText = getQuestionText(mapping.toolId);

        // Only add questions with actual text content (not just IDs)
        if (questionText && questionText.trim() !== "") {
          // Only add if doesn't contain ID pattern (e.g., "ID: 123")
          if (!questionText.match(/^ID:\s*\d+$/i)) {
            observationList.push(questionText);
          }
        }
      });
    }

    // Prepare observation items (images and/or descriptions)
    const observationItems: { imageUrl: string | null; description: string }[] = [];
    
    console.log("Processing walkData images and descriptions:", {
      hasMiscellImage: !!walkData.miscellImage,
      hasMiscellImage2: !!walkData.miscellImage2,
      hasMiscellImageDesc: !!walkData.miscellImageDesc,
      hasMiscellImage2Desc: !!walkData.miscellImage2Desc,
      miscellImageType: walkData.miscellImage ? (walkData.miscellImage.startsWith('http') ? 'URL' : 'Base64') : null,
      miscellImage2Type: walkData.miscellImage2 ? (walkData.miscellImage2.startsWith('http') ? 'URL' : 'Base64') : null,
      miscellImagePreview: walkData.miscellImage ? walkData.miscellImage.substring(0, 80) + "..." : null,
      miscellImage2Preview: walkData.miscellImage2 ? walkData.miscellImage2.substring(0, 80) + "..." : null
    });

    // Add first observation item (image and/or description)
    if (walkData.miscellImage || walkData.miscellImageDesc) {
      observationItems.push({
        imageUrl: walkData.miscellImage || null,
        description: walkData.miscellImageDesc || "",
      });
    }
    
    // Add second observation item (image and/or description)
    if (walkData.miscellImage2 || walkData.miscellImage2Desc) {
      observationItems.push({
        imageUrl: walkData.miscellImage2 || null,
        description: walkData.miscellImage2Desc || "",
      });
    }

    // Add walkImages if they exist - check for both possible property names
    console.log("Checking for leadership images in data:", {
      hasLeadershipWalkImages: !!data.leadershipWalkImages,
      hasLeadershipImages: !!data.leadershipImages,
      walkImagesLength: data.leadershipWalkImages?.length,
      imagesLength: data.leadershipImages?.length,
    });

    const leadershipImages = data.leadershipWalkImages || data.leadershipImages;
    if (
      leadershipImages &&
      Array.isArray(leadershipImages) &&
      leadershipImages.length > 0
    ) {
      console.log(`Found ${leadershipImages.length} leadership images`);

      leadershipImages.forEach((img: any, index: number) => {
        console.log(`Processing leadership image ${index}:`, {
          hasBase64: !!img.ImageBase64,
          contentType: img.ImageBase64 ? (img.ImageBase64.startsWith('http') ? 'URL' : 'Base64') : null,
          base64Preview: img.ImageBase64 ? img.ImageBase64.substring(0, 80) + "..." : null,
          description: img.Description,
          imageType: img.ImageType,
          fileName: img.FileName,
        });

        if (img.ImageBase64) {
          observationItems.push({
            imageUrl: img.ImageBase64,
            description: img.Description || "",
          });
        }
      });

      console.log(`Total observation items to render: ${observationItems.length}`, observationItems.map((item, idx) => ({
        index: idx,
        hasImageUrl: !!item.imageUrl,
        imageUrlPreview: item.imageUrl ? item.imageUrl.substring(0, 50) + "..." : null,
        description: item.description
      })));

      console.log(`Added ${observationItems.length} observation items to display`);
    }

    // const observationNotes = walkData.notes || data.Notes || "";

    return (
      <ScrollView
        style={[
          styles.scrollContainer,
          { backgroundColor: colors.background.page },
        ]}
      >
        <View style={styles.cardGrid}>
          {/* Observations Card */}
          <View
            style={
              Platform.OS === "web"
                ? {
                    width: "100%",
                    marginBottom: 16,
                  }
                : styles.cardGridItemFullWidth
            }
          >
            <LhCardDisplay
              headerTitle="Leadership Walk Observations"
              displayValue={
                observationList.filter((item) => item && item.trim().length > 0)
                  .length > 0
                  ? observationList
                      .filter((item) => item && item.trim().length > 0)
                      .join(", ") // Comma-separated instead of numbered list
                  : "No observations recorded."
              }
              width="100%"
            />
          </View>

          {/* Notes Card */}
          {/* <View style={styles.cardGridItem}>
            <LhCardDisplay
              headerTitle="Walk Notes"
              displayValue={observationNotes || 'No notes recorded.'}
              width="100%"
            />
          </View> */}

          {/* Miscellaneous Observations Card */}
          <View style={styles.fullWidthCard}>
            <View
              style={[
                styles.card,
                {
                  borderRadius: 8,
                  shadowColor: "#000",
                  shadowOpacity: 0.05,
                  shadowRadius: 4,
                  elevation: 2,
                  backgroundColor: isDarkMode ? colors.background.surface : "white",
                  borderColor: isDarkMode ? colors.border.default : "#e9ecef",
                },
              ]}
            >
              <View
                style={[
                  styles.lhCardHeader,
                  {
                    backgroundColor: colors.brand.primary,
                    borderColor: colors.brand.primary,
                  },
                ]}
              >
                <Text style={[styles.lhCardHeaderText, { color: colors.text.inverse }] }>
                  Walk Observations & Images
                </Text>
              </View>
              {observationItems.length > 0 ? (
                observationItems.map((item, idx) => (
                  <View key={idx} style={styles.imageContainer}>
                    <View style={styles.imageWithDescriptionContainer}>
                      {item.imageUrl ? (
                        renderImage(
                          item.imageUrl,
                          `Walk Image ${idx + 1}`,
                          {
                            height: 160,
                            width: Platform.OS === "web" ? "300px" : "100%",
                            marginTop: 4,
                          }
                        )
                      ) : null}
                      {item.description ? (
                        <Text
                          style={[
                            styles.imageDescription,
                            { color: isDarkMode ? colors.text.inverse : "rgb(51, 51, 51)" },
                          ]}
                        >
                          {item.description}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                ))
              ) : (
                <View
                  style={[
                    styles.noImageContainer,
                    isDarkMode && {
                      backgroundColor: colors.background.elevated,
                      borderColor: colors.border.default,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.noImageText,
                      { color: isDarkMode ? colors.text.inverse : "#6c757d" },
                    ]}
                  >
                    No observations or images available
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    );
  };

  return (
    <LHOverlay
      visible={isOverlayVisible}
      onDismiss={closeOverlay}
      title="Leadership Walk Details"
      size="large"
    >
      {renderContent()}
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    ...(Platform.OS !== "web" && {
      backgroundColor: "white",
    }),
  },
  messageContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  loadingOverlayContainer: {
    flex: 1,
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
  },
  loadingTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0066CC",
    marginTop: 12,
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
  },
  infoText: {
    fontSize: 16,
    color: "#6C757D",
    textAlign: "center",
  },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    ...(Platform.OS !== "web" && {
      borderWidth: Platform.OS === "ios" ? 1 : 0,
      borderColor: "#e9ecef",
    }),
  },
  cardGrid: {
    padding: 16,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Platform.OS === "web" ? 16 : 12,
  },
  cardGridItem: {
    width: "48%",
    marginBottom: 16,
    overflow: "hidden",
    ...(Platform.OS !== "web" && {
      borderWidth: 1,
      borderColor: "#e9ecef",
      borderRadius: 8,
      backgroundColor: "white",
    }),
  },
  cardGridItemFullWidth: {
    width: "100%",
    marginBottom: 16,
    overflow: "hidden",
    ...(Platform.OS !== "web" && {
      borderWidth: 1,
      borderColor: "#e9ecef",
      borderRadius: 8,
      backgroundColor: "white",
    }),
  },
  fullWidthCard: {
    width: "100%",
    marginBottom: 16,
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 4px",
    borderRadius: 8,
    overflow: "hidden",
    ...(Platform.OS !== "web" && {
      borderWidth: 1,
      borderColor: "#e9ecef",
      borderRadius: 8,
    }),
  },
  imagePreview: {
    width: "100%",
    height: 200,
    borderRadius: 4,
    // marginBottom: 8,
  },
  imageContainer: {
    marginBottom: 10,
    padding: 12,
  },
  imageWithDescriptionContainer: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    alignItems: Platform.OS === "web" ? "flex-start" : "center",
    justifyContent: "flex-start",
  },
  imageDescription: {
    fontSize: 16,
    color: "rgb(51, 51, 51)",
    marginTop: 8,
    marginLeft: Platform.OS === "web" ? 12 : 0,
    // fontStyle: "italic",
    padding: 4,
    flex: Platform.OS === "web" ? 1 : 0,
  },
  errorImageContainer: {
    width: "100%",
    height: 150,
    backgroundColor: "#f8d7da",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    // marginBottom: 8,
  },
  errorImageText: {
    color: "#721c24",
    fontWeight: "bold",
  },
  noImageContainer: {
    width: "100%",
    height: 150,
    backgroundColor: "#f8f9fa",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    // marginBottom: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
    borderStyle: "dashed",
  },
  noImageText: {
    color: "#6c757d",
    fontStyle: "italic",
  },
  miscImageContainer: {
    padding: 12,
    marginBottom: 10,
  },
  lhCardHeader: {
    backgroundColor: "rgb(26, 115, 232)",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: "#005bb5",
    // marginBottom: 12,
    ...(Platform.OS === "ios" && {
      borderWidth: 1,
      borderColor: "rgb(26, 115, 232)",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
    }),
  },
  lhCardHeaderText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 18,
    letterSpacing: 0.5,
  },
});

export default LeadershipWalkModal;
